import {
    RiBuilding2Line,
    RiCloseCircleFill,
    RiEdit2Line,
    RiListCheck,
    RiMailLine,
} from "@remixicon/react";
import React, {
    ReactNode,
    useMemo,
} from "react";
import { Paths, useParameterFilters } from "../../../../../contexts/Routing";

import {
    ChannelType, ChatSettings, GetChannelListDocument, GetChatTagSettingsDocument,
    useAddChannelTagToChannelsMutation, useGetChannelListQuery,
} from "../../../../../__generated__/api";
import { ReactComponent as GroupsList } from "../../../../../assets/icons/groups-list.svg";
import { useFormBuilder } from "../../../../../components/dirty-tracker/FormTracker";
import { Filters as FiltersSelector } from "../../../../../components/filters/Filters";
import ChannelTags from "../../../../../components/pickers/channel-tags/ChannelTags";
import Badge from "../../../../../components/theme/badge/Badge";
import { useDrawerForm } from "../../../../../components/theme/drawer";
import createFilterList from "../../../../../components/theme/filter-list/FilterList";
import FormGroup from "../../../../../components/theme/form-group/FormGroup";
import SettingsContainer from "../../../../../components/theme/settings";
import createTableContainer from "../../../../../components/theme/table";
import Text from "../../../../../components/theme/text/Text";
import Styles from "./ChannelList.module.scss";
import { SOURCES } from "../../base";

const FilterList = createFilterList<ChannelType | "all">();

const DISPLAY_TYPES = new Set([ChannelType.Slack, ChannelType.MicrosoftTeams]);

export interface ChatSettingsWithName extends ChatSettings {
    name: string | undefined;
    workspaceName: string | undefined;
    tags: string[];
    tagList: string
}
const TableContainer = createTableContainer<ChatSettingsWithName>();

export function useChannelSettingsList(settings: ChatSettings[] | undefined) {
    return useMemo(() => {
        const workspaces = new Set<string>();
        const groups: Record<ChannelType, number> = Object.create(null);

        const channelList = settings?.mapFilter(setting => {
            if (!DISPLAY_TYPES.has(setting.type ?? ChannelType.InboundSupportEmail)) {
                return undefined;
            }
            const tags = setting.options?.slack?.tags ?? setting.options?.microsoftTeams?.tags ?? [];

            const formatted = {
                ...setting,
                name: setting.options?.slack?.name ?? setting.options?.microsoftTeams?.name ?? "",
                workspaceName: setting.options?.slack?.workspaceName ?? setting.options?.microsoftTeams?.workspaceName ?? "",
                tags,
                tagList: tags.join(", ").toLocaleLowerCase(),
            };

            if (formatted?.workspaceName) {
                workspaces.add(formatted.workspaceName);
            }
            groups[setting.type as ChannelType] = (groups[setting.type as ChannelType] ?? 0) + 1;
            return formatted;
        }) ?? [] as ChatSettingsWithName[];

        return {
            workspacesList: Array.from(workspaces).map(value => ({ label: value, value })),
            shouldShowWorkspaces: workspaces.size > 1,
            groupOptions: groups,
            channelList,
        };
    }, [settings]);
}

export default function ChannelsTable(): ReactNode {
    const { data, error, loading: isLoading } = useGetChannelListQuery();

    const [filters, setFilters] = useParameterFilters(Paths.ADMINISTRATOR_SETTING, {
        source: "all",
    });

    const [addTagToChannels] = useAddChannelTagToChannelsMutation();

    const {
        shouldShowWorkspaces,
        groupOptions,
        workspacesList,
        channelList: settings,
    } = useChannelSettingsList(data?.chatSettingsMany as ChatSettings[]);

    const displayItems = useMemo(
        () => settings.filter(row => !filters.source || filters.source === "all" || row.type === filters.source),
        [filters.source, settings],
    );

    const { properties } = useFormBuilder(
        builder => builder.property("selectedIds", () => new Set<string>()),
        [],
    );

    const { element, setOpen, formTracker } = useDrawerForm({
        title: "Add Channel Group",
        onHide: () => {
            formTracker.reset();
        },
        onSave: async () => {
            await addTagToChannels({
                refetchQueries: [
                    { query: GetChannelListDocument },
                    { query: GetChatTagSettingsDocument },
                ],
                variables: {
                    channelTag: {
                        tag: formTracker.properties.channelTag.value,
                        channelIds: Array.from(properties.selectedIds.value),
                    },

                },
            });
            properties.selectedIds.reset();
            formTracker.reset();
        },
        formTrackerBuilder: {
            handler: builder => builder
                .property<"channelTag", string>("channelTag", () => ""),
            dependencies: [],
        },
        // eslint-disable-next-line react/no-unstable-nested-components
        body: tracker => (
            <FormGroup label="Channel Group Tags" size="medium">
                <ChannelTags
                    value={tracker.properties.channelTag.value}
                    onSelected={tracker.properties.channelTag.setValue}
                />
            </FormGroup>
        ),
    });

    return (
        <>

            <SettingsContainer navigation="none">
                <SettingsContainer.Section title="Channels" shouldDisplayTitle={false}>
                    <SettingsContainer.Setting
                        title="Channel Invites"
                        description="."
                        permissions={SettingsContainer.Setting.Permissions.Administrator}
                        isFullWidth
                    >
                        <TableContainer data={displayItems} isLoading={isLoading} error={error}>
                            <TableContainer.Empty>
                                <TableContainer.Empty.Default
                                    text="Here you can manage all the settings for any of the channels you already
                                    have connected to Foqal. It seems you don't have any channels quite yet. To get
                                    started, add the Foqal Agent app to your Slack or Microsoft Teams channel."
                                    title="Channels"
                                    icon={<RiListCheck />}
                                />
                            </TableContainer.Empty>
                            <TableContainer.Header>
                                <FilterList
                                    selectedItem={filters.source as ChannelType ?? "all"}
                                    onChangeSelected={key => setFilters("source", key)}
                                >
                                    <FilterList.Button filterItem="all" text="All" count={settings.length} />
                                    {Object.entries(groupOptions).map(([type, count]) => {
                                        const source = SOURCES[type as ChannelType];
                                        return source ? (
                                            <FilterList.Button
                                                filterItem={type as ChannelType}
                                                text={source.text}
                                                count={count}
                                                key={type}
                                                prefixIcon={source.filterIcon}
                                            />
                                        ) : undefined;
                                    })}
                                </FilterList>
                            </TableContainer.Header>
                            <TableContainer.TableControls>

                                <TableContainer.TableControls.Search
                                    search={(query, item) => item.name?.toLocaleLowerCase().includes(query)
                                            || item.tagList?.toLocaleLowerCase().includes(query)
                                            || (shouldShowWorkspaces && item.workspaceName?.toLocaleLowerCase().includes(query))
                                            || item.type?.toLocaleLowerCase().includes(query)}
                                />
                                {properties.selectedIds.value.size > 0 ? (
                                    <>
                                        <TableContainer.TableControls.Button
                                            variant="outline-blue"
                                            postfixIcon={<RiCloseCircleFill />}
                                            onClick={() => properties.selectedIds.setValue(new Set())}
                                            size="small"
                                            className={Styles["button-clear"]}
                                            text={`${properties.selectedIds.value.size} selected`}
                                        />
                                        <TableContainer.TableControls.Seperator />
                                        <TableContainer.TableControls.Create
                                            variant="outline-gray"
                                            onClick={() => setOpen(true)}
                                            size="small"
                                            text="Add Channel Group"
                                        />
                                    </>

                                ) : (
                                    <>

                                        <TableContainer.TableControls.Seperator />
                                        <TableContainer.TableControls.Filters />

                                        <TableContainer.TableControls.Filter
                                            defaultValue={new Set<string>()}
                                            deserialize={TableContainer.TableControls.Filter.Serializers.deserializeSet}
                                            serialize={TableContainer.TableControls.Filter.Serializers.serializeSet}
                                            filterKey="workspace"
                                            onFilter={(item, selected) => selected.has(item.workspaceName ?? "")}
                                            isEqual={(left, right) => Array.from(left ?? []).equals(Array.from(right ?? []))}
                                        >
                                            {(currentValue, setValue) => (
                                                <FiltersSelector
                                                    toggleText="Workspace"
                                                    categories={workspacesList}
                                                    icon={<RiBuilding2Line />}
                                                    searchMinimum={5}
                                                    selectedCategories={currentValue}
                                                    onSelected={setValue}
                                                />
                                            )}

                                        </TableContainer.TableControls.Filter>

                                        <TableContainer.TableControls.Filter
                                            defaultValue={new Set<string>()}
                                            deserialize={TableContainer.TableControls.Filter.Serializers.deserializeSet}
                                            serialize={TableContainer.TableControls.Filter.Serializers.serializeSet}
                                            filterKey="channelTags"
                                            isEqual={(left, right) => Array.from(left ?? []).equals(Array.from(right ?? []))}
                                            onFilter={(item, selected) => selected.size === 0
                                                || item.tags.some(tag => selected.has(tag))}
                                        >
                                            {(currentValue, setValue) => (
                                                <ChannelTags
                                                    isMulti
                                                    toggleText="Channel Groups"
                                                    icon={<GroupsList />}
                                                    searchMinimum={5}
                                                    shouldShowDropdown
                                                    canSelect
                                                    onSelected={values => setValue(new Set(values))}
                                                    value={Array.from(currentValue)}
                                                />
                                            )}

                                        </TableContainer.TableControls.Filter>

                                        <TableContainer.TableControls.SortBy />
                                    </>
                                )}
                            </TableContainer.TableControls>
                            <TableContainer.Table>
                                <TableContainer.RowSelector
                                    selection={properties.selectedIds.value}
                                    onSelectionChange={selection => {
                                        properties.selectedIds.setValue(selection);
                                    }}
                                />

                                <TableContainer.TableValue
                                    columnKey="name"
                                    title="Name"
                                    sortBy={item => item.name?.toLowerCase()}
                                >
                                    {item => item.name}
                                </TableContainer.TableValue>
                                <TableContainer.TableValue
                                    columnKey="source"
                                    title="Source"
                                    sortBy={item => item.type?.toLowerCase()}
                                >
                                    {item => {
                                        const source = SOURCES[item.type as ChannelType];
                                        return (
                                            <Text
                                                textVariant="body-2-regular"
                                            >
                                                {source.tableIcon}
                                                {" "}
                                                {source.text}
                                            </Text>
                                        );
                                    }}
                                </TableContainer.TableValue>
                                {shouldShowWorkspaces && (
                                    <TableContainer.TableValue
                                        columnKey="workspace"
                                        title="Workspace"
                                        sortBy={item => item.workspaceName?.toLowerCase()}
                                    >
                                        {item => <Badge variant="primary">{item.workspaceName}</Badge>}
                                    </TableContainer.TableValue>
                                )}

                                <TableContainer.TableValue
                                    columnKey="channelTags"
                                    title="Channel Groups"
                                    sortBy={item => item.tagList}
                                >
                                    {item => item.tags.map(tag => (
                                        <Badge variant="gray" key={tag} className="m3e-2">{tag}</Badge>
                                    ))}
                                </TableContainer.TableValue>

                                <TableContainer.Action
                                    icon={<RiEdit2Line />}
                                    altText="Edit Channel Settings"
                                    to={Paths.ADMINISTRATOR_SETTING_ID}
                                    getLinkParams={item => ({ setting: "chat", id: item.id })}
                                />

                            </TableContainer.Table>
                        </TableContainer>
                    </SettingsContainer.Setting>

                </SettingsContainer.Section>
            </SettingsContainer>
            {element}
        </>
    );
}
